import { modelState } from '@/utils/modelState';

export interface SourceChoice { key: string; label: string; requiresDetails: boolean; enabled: boolean }
export interface SourceValue { sourceKey: string; detail: string | null }
export interface SourceData { choices: SourceChoice[]; value: SourceValue | null; isForm: boolean }
export interface SourceSnapshot extends Omit<SourceData, 'value'> { value: SourceValue; loading: boolean; error: string | null }

export function sourceValidation(value: SourceValue, choices: SourceChoice[]): string | null {
    const choice = choices.find(c => c.key === value.sourceKey);
    if (!choice) return 'Choose an inquiry source.';
    if (choice.requiresDetails && !value.detail?.trim()) return 'Enter details for this inquiry source.';
    if ((value.detail?.trim().length ?? 0) > 200) return 'Inquiry source details must be at most 200 characters.';
    return null;
}

/** Native model state owns attribution. Only new editors need a separate catalog read. */
export function createInquirySourceState(data?: SourceData, request: typeof fetch = fetch) {
    const Value = modelState<SourceValue>(data?.value ?? { sourceKey: 'unknown', detail: null },
        (left, right) => left.sourceKey === right.sourceKey && left.detail === right.detail);
    let snapshot: SourceSnapshot = { choices: data?.choices ?? [], value: Value.value,
        isForm: data?.isForm ?? false, loading: !data?.choices.length, error: null };
    const subscribers = new Set<() => void>();
    let controller: AbortController | null = null;
    const publish = (next: SourceSnapshot) => { snapshot = next; subscribers.forEach(fn => fn()); };
    const detach = Value.subscribe(() => publish({ ...snapshot, value: Value.value }));
    const load = async () => {
        if (snapshot.choices.length && !snapshot.error) return;
        controller?.abort();
        controller = new AbortController();
        const cancelled = controller.signal;
        const signal = AbortSignal.any([cancelled, AbortSignal.timeout(15000)]);
        publish({ ...snapshot, loading: true, error: null });
        try {
            const response = await request('/api/reservations/inquiry-sources', { signal });
            if (!response.ok) throw new Error('Could not load inquiry sources. Retry before saving.');
            const result = await response.json() as { choices: SourceChoice[] };
            if (!signal.aborted) publish({ ...snapshot, choices: result.choices, loading: false, error: null });
        } catch (error) {
            if (!cancelled.aborted) publish({ ...snapshot, loading: false, error: error instanceof Error ? error.message : String(error) });
        }
    };
    return {
        Value,
        getSnapshot: () => snapshot,
        subscribe: (fn: () => void) => { subscribers.add(fn); return () => { subscribers.delete(fn); }; },
        load,
        validation: () => snapshot.loading ? 'Inquiry sources have not finished loading. Retry before saving.'
            : snapshot.error ?? sourceValidation(snapshot.value, snapshot.choices),
        cancelLoad: () => controller?.abort(),
        dispose: () => { controller?.abort(); detach(); subscribers.clear(); },
        change: (value: SourceValue) => {
            if (snapshot.isForm) return;
            const choice = snapshot.choices.find(c => c.key === value.sourceKey);
            Value.setValue({ sourceKey: value.sourceKey, detail: choice?.requiresDetails ? value.detail : null });
        }
    };
}
export type InquirySourceState = ReturnType<typeof createInquirySourceState>;
